import { useAuth } from "@/auth/AuthContext";
import { cn } from "@/lib/cn";
import { useTheme, type ThemePreference } from "@/themes/theme";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";

const THEMES: { mode: ThemePreference; label: string; icon: ReactNode }[] = [
  {
    mode: "light",
    label: "Hell",
    icon: (
      <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" className="h-4 w-4">
        <circle cx="8" cy="8" r="3" />
        <path d="M8 1v1.5M8 13.5V15M1 8h1.5M13.5 8H15M3 3l1 1M12 12l1 1M3 13l1-1M12 4l1-1" />
      </svg>
    ),
  },
  {
    mode: "dark",
    label: "Dunkel",
    icon: (
      <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" className="h-4 w-4">
        <path d="M13.5 9.5A5.5 5.5 0 0 1 6.5 2.5a5.5 5.5 0 1 0 7 7Z" />
      </svg>
    ),
  },
  {
    mode: "system",
    label: "Automatisch",
    icon: (
      <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" className="h-4 w-4">
        <rect x="1.5" y="2.5" width="13" height="9" rx="1" />
        <path d="M5.5 14h5M8 11.5V14" />
      </svg>
    ),
  },
];

/**
 * Account row at the foot of the sidebar; opens upwards with the
 * light/dark/system theme choice and sign-out.
 */
export function AccountMenu() {
  const { user, logout } = useAuth();
  const { preference, setPreference } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onDoc(event: MouseEvent) {
      if (!ref.current?.contains(event.target as Node)) setOpen(false);
    }
    function onKey(event: KeyboardEvent) {
      if (event.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const name = user?.display_name || user?.principal || "";
  const initials =
    name
      .split(/[\s._@-]+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0])
      .join("")
      .toUpperCase() || "?";

  async function handleLogout() {
    await logout();
    navigate("/login", { replace: true });
  }

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        aria-label="Kontomenü"
        aria-expanded={open}
        className={cn(
          "flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left hover:bg-surface-subtle",
          open && "bg-surface-subtle",
        )}
        onClick={() => setOpen((value) => !value)}
      >
        <span className="flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-full bg-accent-dim text-[10px] font-semibold text-accent">
          {initials}
        </span>
        <span className="flex min-w-0 flex-1 flex-col">
          <span className="truncate text-[12.5px] font-semibold text-ink">{name}</span>
          <span className="truncate text-[11px] text-muted" title={user?.email}>
            {user?.email || user?.principal}
          </span>
        </span>
        <svg
          viewBox="0 0 10 10"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          className={cn("h-2.5 w-2.5 shrink-0 text-muted transition-transform", open && "rotate-180")}
          aria-hidden
        >
          <path d="M2 6.5l3-3 3 3" />
        </svg>
      </button>
      {open ? (
        <div className="absolute bottom-full left-0 right-0 z-30 mb-1 rounded-md border border-hairline bg-surface py-1 shadow-lg">
          <p className="px-3 pb-1 pt-2 text-[10px] font-medium uppercase tracking-wide text-muted">
            Darstellung
          </p>
          {THEMES.map((item) => (
            <button
              key={item.mode}
              type="button"
              className={cn(
                "flex w-full items-center gap-2.5 px-3 py-1.5 text-left text-sm",
                preference === item.mode ? "bg-accent-dim text-accent" : "text-ink hover:bg-surface-subtle",
              )}
              aria-pressed={preference === item.mode}
              onClick={() => setPreference(item.mode)}
            >
              <span className={preference === item.mode ? "text-accent" : "text-muted"}>{item.icon}</span>
              <span className="flex-1">{item.label}</span>
              {preference === item.mode ? <span aria-hidden>✓</span> : null}
            </button>
          ))}

          <div className="my-1 border-t border-hairline" />
          <button
            type="button"
            className="flex w-full items-center gap-2.5 px-3 py-1.5 text-left text-sm text-danger hover:bg-surface-subtle"
            onClick={() => void handleLogout()}
          >
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" className="h-4 w-4">
              <path d="M6 14H3.5a1 1 0 0 1-1-1V3a1 1 0 0 1 1-1H6M10.5 11 14 8l-3.5-3M14 8H6" />
            </svg>
            Abmelden
          </button>
        </div>
      ) : null}
    </div>
  );
}
